<template>
  <ModalDialog contentClass="deletion-modal-content" :canBeClosed="false" open>
    <template v-slot:header>
      <h3>Ingredients Deletion</h3>
    </template>
    <template v-slot:content>
      <p data-cy="deletion-modal-message">
        Adding ingredients to cart for deletion. Please wait
      </p>
      <Loader size="large" data-cy="deletion-modal-loader" />
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import type { IdAndName } from "@/Domain/IdAndName";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { onMounted } from "vue";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { Loader } from "@progress/kendo-vue-indicators";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useScheduleAStore } from "@/stores/useScheduleAStore";

const main = useMainStore();
const scheduleStore = useScheduleAStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  plants: IdAndName[];
  ingredientIds: number[];
}>();

const emit = defineEmits<{
  (e: "closeModal", cartCount: number): void;
}>();

const closeModal = function (cartCount: number = -1) {
  if (cartCount === -1) {
    notificationStore.notify({
      type: NotificationTypeEnum.error,
      message: "Error adding ingredients to delete cart. Please try again.",
      duration: 5000,
    });
    emit("closeModal", cartCount);
  } else {
    notificationStore.notify({
      type: NotificationTypeEnum.success,
      message: `Ingredients added to the delete cart. ${cartCount} ingredient(s) in the cart`,
      duration: 5000,
    });
    scheduleStore.setCartCount(SubApplicationType.Terminate, cartCount);
  }

  emit("closeModal", cartCount);
};

const copyOrDeleteExistingIngredientAsync = async function () {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  return handleResponsePromiseAsync(
    api.get("VueCopyOrDeleteExistingIngredient", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      subApplication: "delete",
      usedIn1Id: props.ingredientIds.join(";"),
      ownsId: "",
      plantName: "",
    }),
    (data) => {
      closeModal(data);
    },
    "DeletionModal.vue",
    "copyOrDeleteExistingIngredientAsync",
    (error) => {
      console.log(error);
      closeModal();
    }
  );
};

onMounted(async () => {
  await copyOrDeleteExistingIngredientAsync();
});
</script>

<style lang="scss">
.deletion-modal-content {
  max-width: 600px;

  p {
    text-align: center;
    font-weight: bold;
    margin: 1.5rem;
  }

  .k-loader {
    display: block;
    margin: 0 auto;

    &-canvas {
      margin: 0 auto;
    }
  }
}
</style>
